(ns minorodata.components.connections.connector-picker
  (:require
   ["lucide-react" :refer [Search]]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.app.utils :as app.utils]
   [minorodata.components.connections.connector-filters :as connector-filters]
   [minorodata.components.spinner.core :as c.spinner]
   [re-posh.core :as re-posh]
   [reagent.core :as reagent]
   [ui.dialog :as dialog]
   [ui.fields :as fields]
   [ui.tabs :as tabs]
   [ui.typography :as typography]
   [ui.utils :refer [clsx]]))

(defn select-connector [{:keys [id]}]
  (dispatch [:connector/add {:connector/action :create-source
                             :connector/type   id}]))

(defn connector-row [{:keys [connector on-select]}]
  (let [{:keys [image-source id name enabled description]} connector]
    [:button {:type     "button"
              :data-cy  (str "connector-picker-item-" id)
              :disabled (not enabled)
              :on-click #(on-select connector)
              :class    (clsx "w-full h-[72px] shrink-0 flex items-center gap-5 px-5 text-left border border-kleene-layout-border rounded-md bg-kleene-background transition-colors overflow-hidden"
                              (if enabled
                                "hover:bg-kleene-hover-background cursor-pointer"
                                "opacity-50 cursor-not-allowed"))}
     [:div {:class "w-20 h-full shrink-0 flex items-center justify-center"}
      [:img {:src   (app.utils/maybe-local-url image-source)
             :alt   name
             :class "max-w-16 h-9 object-contain"}]]
     [:div {:class "min-w-0 flex-1 overflow-hidden"}
      [typography/h4 {:class "text-sm leading-tight truncate"} name]
      [typography/p {:class "text-kleene-text-secondary text-xs leading-snug line-clamp-2"}
       description]]]))

(defn connector-picker []
  (let [kind        (reagent/atom "all")
        category    (reagent/atom "all")
        search-term (reagent/atom "")]
    (reagent/create-class
     {:display-name "connector-picker"

      :component-did-mount
      (fn []
        (dispatch [:connections.api/fetch-user-connectors]))

      :render
      (fn []
        (let [loading?               @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-user-connectors])
              all-connectors         @(re-posh/subscribe [:connections.subs/current-connectors])
              connectors-to-display  (-> all-connectors
                                         connector-filters/filter-enabled
                                         (connector-filters/filter-by-kind @kind)
                                         (connector-filters/filter-sources @category)
                                         (connector-filters/search-sources @search-term)
                                         connector-filters/sort-sources)]
          [:div {:class "flex flex-col w-[72vw] max-w-[900px] min-w-[720px]"}
           [:div
            {:class
             (str
              "mb-3 flex min-h-12 w-full items-end gap-6 "
              "border-b border-kleene-layout-border")}
            [tabs/tabs-root {:value           @kind
                             :on-value-change #(reset! kind %)}
             [tabs/tabs-list {:variant :underlined
                              :class   "border-b-0"}
              (for [{:keys [value label]} connector-filters/kind-tabs]
                ^{:key value}
                [tabs/tabs-trigger {:value value
                                    :variant :underlined}
                 label])]]

            [:div {:class "ml-auto mb-2 shrink-0"}
             [fields/input {:placeholder "Search..."
                            :icon        Search
                            :data-cy     "connector-picker-search"
                            :class       "h-8 w-64 text-xs"
                            :value       @search-term
                            :on-change   #(reset! search-term (.. % -target -value))}]]]

           [tabs/tabs {:value           @category
                       :classes         {:root    "mb-5 overflow-x-auto scrollbar-dark dark:scrollbar py-px scrollbar-thin"
                                         :trigger "shrink-0 text-[11px] px-3 py-1.5"}
                       :data-cy         "connector-picker-category-filter"
                       :on-value-change #(reset! category %)
                       :tabs            connector-filters/connector-tabs}]

           [:div {:class "h-[520px] overflow-y-auto pr-1"
                  :data-cy "connector-picker-list"}
            (cond
              loading?
              [:div {:class "h-full flex flex-col gap-4 items-center justify-center"}
               [c.spinner/spinner-lucide {:size 32}]
               [typography/p "Loading connectors..."]]

              (empty? connectors-to-display)
              [:div {:class "h-full flex items-center justify-center"}
               [typography/p "No matching connectors found."]]

              :else
              [:div {:class "flex flex-col gap-2"}
               (for [connector connectors-to-display]
                 ^{:key (:id connector)}
                 [dialog/dialog-close {:as-child true}
                  [connector-row {:connector connector :on-select select-connector}]])])]]))})))

(defn add-source-dialog
  [{:keys [trigger]}]
  [dialog/dialog-root
   [dialog/dialog-trigger {:as-child true} trigger]

   [dialog/dialog-content
    {:size          :large
     :class         (str
                     "w-[78vw] max-w-[980px] min-w-[760px] "
                     "overflow-hidden p-8")
     :overlay-class "backdrop-blur-[2px]"}

    [dialog/dialog-title
     {:class "mb-4 pr-10"}
     "Available connectors"]

    [connector-picker]]])
